import React, { useState } from 'react';
import { Flame, CheckCircle2, ChevronRight, ChevronDown, Sparkles, Brain, Dumbbell, BookOpen, RotateCcw } from 'lucide-react';

const fmtMonthYear = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } catch (e) {
    return '';
  }
};

const Overline = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-400">{children}</p>
);

const ActiveJourneyCard = ({ journey, onOpen, onQuickLog }) => (
  <div
    data-testid={`active-journey-${journey.id}`}
    className="bg-white rounded-2xl border border-neutral-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 p-6 flex flex-col gap-4"
  >
    <div className="flex items-center justify-between">
      <span className="bg-neutral-100 text-neutral-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
        <Brain size={13} /> Habit
      </span>
      {journey.streak > 0 && (
        <span className="inline-flex items-center gap-1 text-orange-600 font-bold text-sm">
          <Flame size={16} /> {journey.streak}
        </span>
      )}
    </div>

    <div>
      <h3 className="text-lg font-semibold text-neutral-900 leading-snug">
        {journey.tiny_win || journey.identity_goal}
      </h3>
      {journey.identity_goal && (
        <p className="text-sm text-neutral-500 mt-1 line-clamp-2">
          Becoming someone who {journey.identity_goal}
        </p>
      )}
    </div>

    <div>
      <div className="flex justify-between text-xs text-neutral-500 mb-1.5">
        <span>Progress</span>
        <span className="font-medium">Day {journey.day_count} of {journey.target_days}</span>
      </div>
      <div className="w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-500 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${journey.progress}%` }}
        />
      </div>
    </div>

    <div className="flex gap-2 mt-1">
      <button
        data-testid={`quick-log-${journey.id}`}
        onClick={() => onQuickLog(journey)}
        className="flex-1 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-full px-4 py-2.5 font-semibold text-sm transition-all"
      >
        ✓ Log today
      </button>
      <button
        data-testid={`details-${journey.id}`}
        onClick={() => onOpen(journey.id)}
        className="bg-white border-2 border-neutral-200 hover:border-emerald-500 text-neutral-700 hover:text-emerald-700 rounded-full px-4 py-2.5 font-semibold text-sm transition-all inline-flex items-center gap-1"
      >
        Details <ChevronRight size={16} />
      </button>
    </div>
  </div>
);

const FitnessCard = ({ goal, today, onOpen, onFitnessLog }) => (
  <div data-testid="dashboard-fitness-card" className="bg-white rounded-2xl border border-neutral-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 p-6 flex flex-col gap-4">
    <div className="flex items-center justify-between">
      <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
        <Dumbbell size={13} /> Fitness
      </span>
      {(goal.current_streak || 0) > 0 && (
        <span className="inline-flex items-center gap-1 text-orange-600 font-bold text-sm">
          <Flame size={16} /> {goal.current_streak}
        </span>
      )}
    </div>
    <h3 className="text-lg font-semibold text-neutral-900 leading-snug">{goal.goal_name}</h3>

    {today && (
      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
        <p className="text-xs text-emerald-700/70 capitalize mb-1">{today.day}&apos;s activity</p>
        {today.is_rest_day ? (
          <p className="text-base font-medium text-neutral-700">😴 Rest day — recovery counts.</p>
        ) : (
          <>
            <p className="text-base font-bold text-neutral-900 mb-3">{today.activity}</p>
            {today.logged ? (
              <span className="text-emerald-700 text-sm font-medium">
                {today.completed ? '✅ Completed today' : '⏭️ Skipped today'}
              </span>
            ) : (
              <div className="flex gap-2">
                <button
                  data-testid="fitness-quick-done"
                  onClick={() => onFitnessLog(true)}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-full py-2 text-sm font-semibold transition-all"
                >
                  ✓ Done
                </button>
                <button
                  data-testid="fitness-quick-skip"
                  onClick={() => onFitnessLog(false)}
                  className="px-4 bg-neutral-100 hover:bg-neutral-200 text-neutral-600 rounded-full py-2 text-sm font-medium transition-all"
                >
                  Skip
                </button>
              </div>
            )}
          </>
        )}
      </div>
    )}

    <button
      data-testid="fitness-card-details"
      onClick={() => onOpen(goal.id)}
      className="text-emerald-600 hover:text-emerald-800 text-sm font-semibold inline-flex items-center gap-1 self-start"
    >
      View planner <ChevronRight size={16} />
    </button>
  </div>
);

const GraduatedCard = ({ journey, onOpen }) => (
  <div data-testid={`graduated-${journey.id}`} className="bg-emerald-50 rounded-2xl border border-emerald-100 p-5 flex flex-col gap-1.5">
    <div className="flex items-center gap-2">
      <CheckCircle2 size={18} className="text-emerald-600" />
      <span className="text-xs uppercase tracking-[0.15em] font-bold text-emerald-700">Mastered</span>
    </div>
    <h4 className="font-semibold text-neutral-900 leading-snug">{journey.target_habit || journey.identity_goal}</h4>
    <p className="text-xs text-neutral-500">Completed {fmtMonthYear(journey.completed_at || journey.created_at)}</p>
    <button
      data-testid={`graduated-view-${journey.id}`}
      onClick={() => onOpen(journey.id)}
      className="text-emerald-600 hover:text-emerald-800 text-xs font-semibold inline-flex items-center gap-1 mt-1 self-start"
    >
      View journey <ChevronRight size={14} />
    </button>
  </div>
);

const ArchiveRow = ({ journey, onOpen }) => (
  <button
    data-testid={`archive-row-${journey.id}`}
    onClick={() => onOpen(journey.id)}
    className="w-full text-left flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-emerald-50 transition group"
  >
    <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-medium text-neutral-800 truncate">{journey.target_habit || journey.identity_goal}</span>
      <span className="block text-xs text-neutral-400">{fmtMonthYear(journey.completed_at || journey.created_at)}</span>
    </span>
    <ChevronRight size={14} className="text-neutral-300 group-hover:text-emerald-500 shrink-0" />
  </button>
);

const PausedRow = ({ journey, onResume }) => (
  <div data-testid={`paused-row-${journey.id}`} className="flex items-center gap-2 py-2.5 px-3">
    <span className="flex-1 min-w-0 text-sm font-medium text-neutral-700 truncate">{journey.tiny_win || journey.identity_goal}</span>
    <button
      data-testid={`resume-${journey.id}`}
      onClick={() => onResume(journey.id)}
      className="text-emerald-600 hover:text-emerald-800 text-xs font-semibold inline-flex items-center gap-1 shrink-0"
    >
      <RotateCcw size={12} /> Resume
    </button>
  </div>
);

const Collapsible = ({ title, count, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden" data-testid={testid}>
      <button
        data-testid={`${testid}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50 transition"
      >
        <span className="text-sm font-semibold text-neutral-700">
          {title} <span className="text-neutral-400 font-normal">({count})</span>
        </span>
        <ChevronDown size={18} className={`text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-1.5 pb-2 border-t border-neutral-100 divide-y divide-neutral-50">{children}</div>}
    </div>
  );
};

const MemberHome = ({
  user, grouped, fitnessGoal, fitnessToday, note, bookUrl,
  onOpenJourney, onQuickLog, onFitnessLog, onResume, onStartNewJourney,
}) => {
  const active = grouped?.active || [];
  const graduated = grouped?.graduated || [];
  const paused = grouped?.paused || [];
  const hasFocus = active.length > 0 || !!fitnessGoal;
  const firstName = (user?.name || '').split(' ')[0] || 'there';

  return (
    <div data-testid="member-dashboard">
      {/* Personalized greeting */}
      <div className="mb-6">
        <Overline>Your Dashboard</Overline>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 mt-1">
          Welcome back, {firstName}.
        </h1>
      </div>

      {/* Notes from the Journey */}
      {note && (
        <div className="mb-8 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-2xl p-5 sm:p-6 border border-amber-200/50">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={16} className="text-amber-700" />
            <h3 className="text-amber-800 font-semibold tracking-wide text-xs uppercase">Notes from the Journey</h3>
          </div>
          <p className="text-neutral-700 leading-relaxed text-lg pl-3 border-l-4 border-amber-300" style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic' }}>
            &ldquo;{note}&rdquo;
          </p>
          <div className="mt-3 flex items-center justify-between pl-3">
            <span className="text-amber-700 text-sm" style={{ fontFamily: "'Georgia', serif" }}>— Pam Matambanadzo</span>
            {bookUrl && (
              <a href={bookUrl} target="_blank" rel="noopener noreferrer" className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-full transition">
                📚 Get the Book
              </a>
            )}
          </div>
        </div>
      )}

      {/* Side-by-side scanning layout */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start">
        {/* LEFT: Current Focus */}
        <div className="md:col-span-8 flex flex-col gap-5">
          <Overline>Current Focus</Overline>
          {hasFocus ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {active.map((j) => (
                <ActiveJourneyCard key={j.id} journey={j} onOpen={onOpenJourney} onQuickLog={onQuickLog} />
              ))}
              {fitnessGoal && (
                <FitnessCard goal={fitnessGoal} today={fitnessToday} onOpen={onOpenJourney} onFitnessLog={onFitnessLog} />
              )}
            </div>
          ) : (
            /* Celebratory welcome-back empty state */
            <div className="relative rounded-2xl overflow-hidden border border-emerald-100" data-testid="welcome-back-empty">
              <img
                src="https://images.unsplash.com/photo-1522075782449-e45a34f1ddfb?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200"
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/40" />
              <div className="relative p-8">
                <Sparkles size={28} className="text-emerald-600 mb-3" />
                <h2 className="text-2xl font-bold text-neutral-900">You&apos;ve come a long way.</h2>
                <p className="text-neutral-600 mt-2 max-w-md">
                  {graduated.length > 0
                    ? `You've mastered ${graduated.length} journey${graduated.length > 1 ? 's' : ''}. There's no day-one here — just your next chapter, whenever you're ready.`
                    : "Your active journeys will live here. Ready to begin?"}
                </p>
                <button
                  data-testid="empty-start-journey"
                  onClick={onStartNewJourney}
                  className="mt-5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-full px-6 py-3 font-semibold transition-all"
                >
                  Start a new journey →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Past Achievements */}
        <div className="md:col-span-4 flex flex-col gap-4">
          <Overline>Past Achievements</Overline>
          {graduated.length > 0 ? (
            <>
              {/* Most recent mastered journey, in full */}
              <GraduatedCard journey={graduated[0]} onOpen={onOpenJourney} />
              {/* Everything else tucked into a collapsible Archive */}
              {graduated.length > 1 && (
                <Collapsible title="The Archive" count={graduated.length - 1} testid="archive-section">
                  {graduated.slice(1).map((j) => (
                    <ArchiveRow key={j.id} journey={j} onOpen={onOpenJourney} />
                  ))}
                </Collapsible>
              )}
            </>
          ) : (
            <p className="text-sm text-neutral-400 bg-white border border-neutral-200 rounded-2xl p-5">
              Your mastered journeys will be celebrated here. 🌱
            </p>
          )}

          {paused.length > 0 && (
            <Collapsible title="Paused" count={paused.length} testid="paused-section">
              {paused.map((j) => (
                <PausedRow key={j.id} journey={j} onResume={onResume} />
              ))}
            </Collapsible>
          )}
        </div>
      </div>

      {/* Next Steps */}
      <div className="mt-8 bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-100 p-8 text-center" data-testid="next-steps-section">
        <Overline>Next Steps</Overline>
        <h3 className="text-2xl font-bold text-neutral-900 mt-2">Ready to level up?</h3>
        <p className="text-neutral-600 mt-2 max-w-lg mx-auto">
          {graduated.length > 0
            ? "You've proven you can show up. Start your next chapter with the Coach — we'll build on what you've already become."
            : "When you're ready, the Coach will help you design one tiny, doable win at a time."}
        </p>
        <button
          data-testid="start-new-journey-cta"
          onClick={onStartNewJourney}
          className="mt-5 bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-full px-7 py-3.5 font-semibold transition-all"
        >
          Start a New Journey with the Coach →
        </button>
      </div>
    </div>
  );
};

export default MemberHome;
